import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthLayout } from '../auth-layout';
import { SignInForm } from '../sign-in-form';
import { useOnAlreadyAuthenticated } from '../useOnAlreadyAuthenticated';
import { useAuthRedirect } from '../useAuthRedirect';
import {
  IdentityProviders,
  useKorintConfig,
  getStoredSession,
  performAnonymousSessionHandoff,
} from 'korint-sdk/react';
import { Text } from '@folio/components/ui/text';

export const SignInView = ({
  logo,
  providers = [],
  showLastLoginHint = false,
}: {
  logo: ReactNode;
  providers: IdentityProviders[];
  showLastLoginHint?: boolean;
}) => {
  const { t } = useTranslation();
  const { isLoading } = useOnAlreadyAuthenticated();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clientOptions } = useKorintConfig();
  // Only used by single-tenant apps, where tenant is always set.
  const tenantId = clientOptions.tenant ?? '';

  // Use shared hook for redirect handling (no token storage, uses sessionStorage)
  const { redirectUrl, redirectSearch } = useAuthRedirect();

  const defaultEmail = searchParams.get('email') ?? undefined;

  if (!defaultEmail) {
    return <Navigate to={{ pathname: '/auth', search: redirectSearch }} replace />;
  }

  if (isLoading) {
    return null;
  }

  return (
    <AuthLayout
      title={t('authSignIn.welcomeTitle')}
      logo={logo}
      footer={
        <div>
          <Text size="sm" className="text-center text-gray-500">
            <Link to={{ pathname: '/auth', search: redirectSearch }} className="text-primary">
              {t('authSignIn.useDifferentEmail')}
            </Link>
          </Text>
        </div>
      }
    >
      <SignInForm
        defaultEmail={defaultEmail}
        onSuccess={async () => {
          if (getStoredSession(tenantId)) {
            await performAnonymousSessionHandoff(tenantId);
          }
          navigate(redirectUrl);
        }}
        providers={providers}
        showLastLoginHint={showLastLoginHint}
      />
    </AuthLayout>
  );
};
